import React, { useMemo, useState } from 'react';
import { Button, Empty, Switch, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ListBase, ListProps, useListContext, useQueryWithStore } from 'react-admin';
import { Link } from 'react-router-dom';
import {
    ColumnHeader,
    FilterField,
    HeaderColumn,
    InfiniteFoot,
    useHashAccordion,
    useHeaderShade,
    useInfinitePages,
    useLinkedPin,
} from '@src/components/Common/DataList';
import MainCard from '@src/components/Common/MainCard';
import { /* OpenRowHint, */ ScrollHint } from '@src/components/Common/OpenCard';
import ServiceTagFilter from '@src/components/Filters/ServiceTagFilter';
import ServiceCard from './ServiceCard';
import { isVisibleCreateServiceButton } from '../utils';
import { ServiceCatalogueConfig } from './types';
import './ServiceCatalogue.css';

const SKELETON_COUNT = 5;

const CatalogueView = ({ config }: { config: ServiceCatalogueConfig }) => {
    const {
        ids, data, loading, total, currentSort,
        page, setPage, perPage,
        filterValues, setFilters,
    } = useListContext();

    // A card is named in the hash by the service's name rather than its id:
    // that is what the search bar links to, and what every link written before
    // these lists were rebuilt points at.
    const hash = useHashAccordion({ aliasOf: (record) => record.name });
    const [dependant, setDependant] = useState(false);

    const canCreate = config.pov === 'serviceowner' && !!isVisibleCreateServiceButton();
    const hasDependant = !!config.dependantResource;

    /**
     * Dependant services come from another endpoint under the same filters —
     * only the resource changes, so the switch reads as a lens over one list
     * rather than a second page.
     */
    const { data: depData, total: depTotal, loading: depLoading } = useQueryWithStore(
        {
            type: 'getList',
            resource: config.dependantResource || '',
            payload: {
                pagination: { page, perPage },
                sort: { field: 'name', order: 'ASC' },
                filter: filterValues,
            },
        },
        { enabled: hasDependant && dependant },
    );

    const own: any[] = useMemo(() => ids.map((id) => data[id]).filter(Boolean), [ids, data]);

    const records: any[] = dependant ? depData || [] : own;
    const isLoading = dependant ? depLoading : loading;
    const count = (dependant ? depTotal : total) ?? 0;

    /* ---- endless scroll ---- */

    // Everything that changes what a page means starts the list over — the
    // dependant lens included, which is a different endpoint under the same
    // filters. Key order in filterValues is not guaranteed between renders, so
    // the keys are sorted rather than stringified as they come.
    const queryKey = JSON.stringify([
        Object.keys(filterValues).sort().map((key) => [key, filterValues[key]]),
        currentSort?.field, currentSort?.order, dependant,
    ]);

    const { items, hasMore, sentinelRef, settling } = useInfinitePages({
        queryKey,
        records,
        loading: isLoading,
        total: count,
        page,
        setPage,
    });

    const openRecord = items.find((record) => hash.isOpen(record));

    // the header shades once services are scrolled under it — it never moves
    const [dock, setDock] = useState<HTMLDivElement | null>(null);
    const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
    useHeaderShade(dock, scroller, 'svc-head-dock');

    /**
     * Everything the API's ServiceFilterSet accepts here. Tags come off their
     * own endpoint rather than a filter list, so that control is the page's
     * own and sits in the bar under its own label.
     */
    const filterFields: FilterField[] = useMemo(
        () => [
            { name: 'name', label: 'Name', type: 'search', placeholder: 'Search by name', dataCy: 'name-search' },
            {
                name: 'owner_id',
                label: 'Service owner',
                type: 'select',
                resource: 'serviceowner_teams',
                // that endpoint has no /filters/ route — only consumer_teams
                // does — so the options come from the list itself
                optionsFrom: 'list',
                dataCy: 'team-selector',
                // a service owner is looking at their own team's services
                hidden: config.pov === 'serviceowner',
            },
            {
                name: 'tags',
                label: 'Tags',
                type: 'custom',
                render: () => <ServiceTagFilter filterValues={filterValues} setFilterValues={setFilters} />,
                width: 260,
                dataCy: 'tag-filter',
            },
        ],
        [config.pov, filterValues, setFilters],
    );

    /** each filter as a header of its own, in the order they read */
    const headerColumns: HeaderColumn[] = useMemo(
        () =>
            filterFields
                .filter((field) => !field.hidden)
                .map((field) => ({ label: field.label, fields: [field], dataCy: field.dataCy })),
        [filterFields],
    );

    const renderBody = () => {
        // `settling` counts as loading: the query just changed, so an empty
        // list means the answer has not arrived, not that there is none
        if ((isLoading || settling) && items.length === 0) {
            return (
                <div className="svc-list">
                    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                        <div key={index} className="svc-skel" />
                    ))}
                </div>
            );
        }

        if (items.length === 0) {
            return (
                <Empty
                    className="svc-empty-state"
                    description={dependant ? 'No dependant services' : 'No services found'}
                />
            );
        }

        // `each` rather than `stagger`: this list grows under the reader as
        // pages load, so only the cards just appended mount and animate
        return (
            <div className="svc-list nx-enter-each">
                {items.map((record) => (
                    <ServiceCard
                        key={record.id}
                        record={record}
                        config={config}
                        dependant={dependant}
                        open={hash.isOpen(record)}
                        toggle={() => hash.toggle(record)}
                        scrollIntoView={hash.scrollsTo(record)}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="netorca-svc netorca-frame">
            <MainCard className="svc-panel">
                <div className="svc-panel__body">
                    {/* The filter bar the table views use, wrapped into a row
                        rather than stood over columns — a catalogue has no
                        columns, but each filter still names itself and opens
                        where it stands. */}
                    <div ref={setDock} className="svc-head-dock">
                        <ColumnHeader
                            className="svc-head"
                            flow
                            columns={headerColumns}
                            schema={filterFields}
                            values={filterValues}
                            onChange={(next) => setFilters(next, {})}
                            dataCy="service-filters"
                        />

                        <span className="svc-head__end">
                            {hasDependant && (
                                <Tooltip title="Services owned by other teams that yours depends on">
                                    <label className="svc-switch">
                                        <Switch
                                            size="small"
                                            checked={dependant}
                                            onChange={(checked) => {
                                                setDependant(checked);
                                                setPage(1);
                                            }}
                                            aria-label="Show dependant services"
                                        />
                                        Dependant
                                    </label>
                                </Tooltip>
                            )}
                            {canCreate && (
                                <Link to="/services/create">
                                    <Button type="primary" size="small" icon={<PlusOutlined />}>
                                        Create service
                                    </Button>
                                </Link>
                            )}
                        </span>
                    </div>

                    {/* The services and the count under them — what scrolls,
                        inside a card that stays put, under a header that never
                        moves (see .svc-scroll in the stylesheet). */}
                    <div ref={setScroller} className="svc-scroll">
                        {/* the circles over the services either side of an open
                            one, which close it — see useExpandedRow. Off now: the list scrolls freely around an open row. OpenRowHint stays, to bring back. */}
                        {/* <OpenRowHint edge="top" noun="service" /> */}
                        {renderBody()}
                        {/* <OpenRowHint edge="bottom" noun="service" /> */}

                        {/* pages load as this comes into view — no page numbers */}
                        <InfiniteFoot
                            sentinelRef={sentinelRef}
                            loading={isLoading}
                            loaded={items.length}
                            total={count}
                            hasMore={hasMore}
                            noun="service"
                        />

                        {/* more services below than the page is showing — while
                            none is open; an open one has its own circles */}
                        <ScrollHint when={!openRecord} />
                    </div>
                </div>
            </MainCard>
        </div>
    );
};

/**
 * The service catalogue. A service owner sees the services their team owns and
 * everything it takes to administer them; a consumer browses what is on offer.
 * Same page, same cards — the config says which of the two it is.
 */
const ServiceCataloguePage = ({ config, listProps }: { config: ServiceCatalogueConfig; listProps: ListProps }) => {
    // a link to a service (#name) puts it first, wherever the alphabet has it
    const pin = useLinkedPin();
    return (
        <ListBase
            // a new link is a new list — see useLinkedPin
            key={pin.pin ?? ''}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            {...listProps}
            filter={{ ...listProps.filter, ...pin }}
        >
            <CatalogueView config={config} />
        </ListBase>
    );
};

export default ServiceCataloguePage;
